import { useEffect, useSyncExternalStore } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { opensAsPage, recordPage } from './open-in';

// Which task is open in the quick view (TF-17): in the address, ?task=<id>. Its own module, so any
// list (and the files of a record, TF-18) can open a task without importing the quick view itself.

const PARAM = 'task';

export function useTaskQuickView() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantId = '' } = useParams();
  const edit = (change: (params: URLSearchParams) => void) => (current: URLSearchParams) => {
    const next = new URLSearchParams(current);
    change(next);
    return next;
  };
  return {
    taskId: params.get(PARAM),
    // As a page instead, when the person chose that in My profile (TF-367).
    open: (id: string) =>
      opensAsPage('task') && tenantId ? navigate(recordPage(tenantId, 'task', id)) : setParams(edit((next) => next.set(PARAM, id)), { state: { taskQuickView: true } }),
    // Opened from the page, it went into the history: going back closes it, so a phone's back button
    // does too. Opened from a link, there is nothing to go back to within the app.
    close: () => {
      if ((location.state as { taskQuickView?: boolean } | null)?.taskQuickView) navigate(-1);
      else setParams(edit((next) => next.delete(PARAM)), { replace: true });
    },
  };
}

// A page that shows the open task in a pane of its own (My tasks on a wide window, TF-98): while it is
// there, the shell's side sheet stays closed.
let hosts = 0;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((listener) => listener());

/** Called by a page while it shows the task itself. */
export function useTaskPaneHost(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    hosts += 1;
    changed();
    return () => {
      hosts -= 1;
      changed();
    };
  }, [active]);
}

/** Whether a page shows the open task itself. */
export function useTaskPaneHosted(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => hosts > 0,
  );
}
